import React, { useEffect, useState } from 'react';
import type { UseFormRegister } from 'react-hook-form';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  getMerchantDetails,
  getProductQueries,
  queryAvailableTypes,
  useAccountStore,
  useProductStore
} from '../../contexts';
import { InputWrapper } from '../atoms';
import { ProductDescriptions } from '../types';

type TProductTypeSelect = {
  merchantId: string;
  register: UseFormRegister<any>;
  name?: string;
  migratedName?: string;
  label?: string;
  migratedLabel?: string;
  supportMigrated?: boolean;
  showMigratedControl?: boolean;
};

const ProductTypeSelect = ({
  name = 'productType',
  migratedName = 'includeMigrated',
  label = 'Product Type',
  migratedLabel = 'Include Migrated Products',
  supportMigrated = true,
  showMigratedControl = true,
  merchantId,
  register
}: TProductTypeSelect) => {
  const [loading, setLoading] = useState(false);
  const setAvailableTypes = useProductStore((s) => s.setAvailableTypes);
  const setAvailableTypesLoading = useProductStore(
    (s) => s.setAvailableTypesLoading
  );
  const availableTypes = useProductStore((s) => s.availableTypes);

  const currentProductIds = useAccountStore((s) => s.currentProductIds);
  const currentProducts = useAccountStore((s) => s.currentProducts);
  const setMerchantDetails = useAccountStore((s) => s.setMerchantDetails);
  const addCurrentProduct = useAccountStore((s) => s.addCurrentProduct);

  const { isLoading: merchantLoading } = getMerchantDetails({
    setMerchantDetails,
    merchantId,
    query: useQuery
  });

  const productQueries = getProductQueries({
    queries: useQueries,
    currentProductIds,
    merchantId,
    addCurrentProduct
  });

  const { isLoading: availableTypesLoading } = queryAvailableTypes({
    setAvailableTypes,
    setAvailableTypesLoading,
    merchantId,
    query: useQuery
  });

  const productTypeOptions = availableTypes
    ?.filter(
      ({ code }) =>
        // only show products that the merchant has access to
        currentProducts?.some(({ productType }) => code === productType) &&
        // allow selection of migrated without the checkbox
        ((supportMigrated && !showMigratedControl) ||
          // @TODO - updated api to provide migrated flag
          !code?.startsWith('Z'))
    )
    .map(({ code, description: descr }) => {
      const description =
        descr ||
        ProductDescriptions[code] ||
        ProductDescriptions[code.replace('Nim', '')] ||
        '';
      return { value: code, text: `${code} - ${description}` };
    });

  useEffect(() => {
    setLoading(
      merchantLoading ||
        availableTypesLoading ||
        productQueries.some((p) => p.isLoading)
    );
  }, [
    merchantId,
    merchantLoading,
    availableTypesLoading,
    productQueries,
    availableTypes,
    currentProductIds,
    currentProducts
  ]);

  return (
    <>
      <InputWrapper
        label={label}
        name={name}
        type="select"
        complexOptions={productTypeOptions}
        loading={loading}
        disabled={loading}
        errorMessage={undefined}
        register={register}
      />
      {showMigratedControl && (
        <InputWrapper
          label={migratedLabel}
          name={migratedName}
          type="boolean"
          errorMessage={undefined}
          register={register}
        />
      )}
    </>
  );
};

export { ProductTypeSelect };
